import { string, number, StringSchema, NumberSchema } from 'yup';
import i18n from 'i18next';
import ipRegex from 'ip-regex';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { TAG_MIN_VALUE, TAG_MAX_VALUE, EMAIL_LIST_DELIMITER } from 'constants/index';

export const createMacAddressValidation = (t: i18n.TFunction): StringSchema => (
    string()
    .matches(/^.(?!1)/i, t('validation.multicastMac'))
    .matches(/^(?!0000\.5e|0200\.5E)/i, t('validation.reservedMac'))
    .matches(/([a-f0-9]){4}\.([a-f0-9]){4}\.([a-f0-9]){4}/i, t('validation.notValidMac'))
);

export const createTagValidation = (t: i18n.TFunction): NumberSchema => (
    number()
    .notOneOf([1, 10], t('validation.vlanTagReserved'))
    .min(TAG_MIN_VALUE, t('validation.vlanRange'))
    .max(TAG_MAX_VALUE, t('validation.vlanRange'))
);

export const createIpValidation = (t: i18n.TFunction): StringSchema => (
    string().matches(ipRegex({ exact: true }), t('validation.ipInvalid'))
);

export const createIpv6Validation = (t: i18n.TFunction): StringSchema => (
    string().matches(ipRegex.v6({ exact: true }), t('validation.ipInvalid'))
);

export const createIpv4AddressOrRangeValidation = (t: i18n.TFunction): StringSchema =>
    string().test(
        'isValidIpv4AddressOrRange',
        t('validation.ipv4Invalid'),
        ipAddressOrRangeTestFunctionFactory({
            ipAddressValidator: (ip) => ipRegex.v4({ exact: true }).test(ip),
            minSubnetMask: 0,
            maxSubnetMask: 32,
        })
    );

export const createIpv6AddressOrRangeValidation = (t: i18n.TFunction): StringSchema =>
    string().test(
        'isValidIpv6AddressOrRange',
        t('validation.ipv6Invalid'),
        ipAddressOrRangeTestFunctionFactory({
            ipAddressValidator: (ip) => ipRegex.v6({ exact: true }).test(ip),
            minSubnetMask: 0,
            maxSubnetMask: 128,
        })
    );

function ipAddressOrRangeTestFunctionFactory({
    ipAddressValidator,
    minSubnetMask,
    maxSubnetMask,
}: {
    ipAddressValidator: (ip: string) => boolean;
    minSubnetMask: number;
    maxSubnetMask: number;
}) {
    return (value?: string) => {
        if (value === undefined || value === null) {
            return true;
        }

        // The number().integer() schema accepts numbers with decimal points if the decimal part is equal to 0.
        // To ensure only whole numbers are accepted (without any decimal part), we need to combine it with
        // string().matches(/^\d*$/) schema that strictly enforces integers.
        const subnetMaskSchema = number().integer().min(minSubnetMask).max(maxSubnetMask);
        const integerSchema = string().matches(/^\d*$/);
        const [ip, subnetMask, ...extra] = value.split('/');
        if (
            ipAddressValidator(ip) &&
            subnetMaskSchema.isValidSync(subnetMask) &&
            integerSchema.isValidSync(subnetMask) &&
            extra.length === 0
        ) {
            return true;
        }
        return false;
    };
}

export const createPhoneNumberValidation = (t: i18n.TFunction): StringSchema => (
    string()
    .test('phoneNumber', t('validation.phoneNumberInvalid'), (value: string): boolean => {
        if (!value) return true;
        const phoneNumber = parsePhoneNumberFromString(value);
        if (phoneNumber) return phoneNumber.isValid();
        return false;
    })
);

export const createLettersValidation = (errorMessage: string): StringSchema => (
    string().matches(/^[A-Za-z]*$/, errorMessage)
);

export const createEmailAddressListValidation = (t: i18n.TFunction): StringSchema =>
    string().test('emailAddressList', t('validation.emailListInvalid'), (value) => {
        if (!value) return true;

        const emails = value.split(EMAIL_LIST_DELIMITER).map((email: string) => email.trim());
        return emails.every((email: string) => string().email().isValidSync(email));
    });
